import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "components/ui/badge";
import { Button } from "@/components/ui/button";
import { ColumnDef } from "@tanstack/react-table";
import { DotsHorizontalIcon } from "@/components/icons/dot-icon";
import { SortUser } from "../users-table/sort-user";
import { ServiceAccountWithCities } from "../../service-accounts.type";
import { DeleteServiceAccountDialog } from "./delete-service-account-dialog";
import { RegenerateKeyDialog } from "./regenerate-key-dialog";
import { ToggleEnabledButton } from "./toggle-enabled-button";

interface ServiceAccountColumnsProps {
  onEdit: (serviceAccount: ServiceAccountWithCities) => void;
  onApiKeyGenerated: (apiKey: string) => void;
}

export const serviceAccountColumns = ({
  onEdit,
  onApiKeyGenerated,
}: ServiceAccountColumnsProps): ColumnDef<ServiceAccountWithCities>[] => [
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => SortUser("Nom", column),
    cell: ({ row }) => {
      const account = row.original;
      return (
        <div className="flex items-center gap-3">
          <div className="flex flex-col bg-transparent border-none p-0 text-left">
            <span className="font-semibold text-xs">{account.name}</span>
            <span className="text-xs text-gray-500">{account.email}</span>
          </div>
        </div>
      );
    },
  },
  {
    id: "email",
    accessorKey: "email",
    header: ({ column }) => SortUser("Email", column),
    cell: ({ row }) => row.original.email,
  },
  {
    id: "citiesDisplay",
    accessorKey: "citiesDisplay",
    enableSorting: false,
    header: () => <span>Villes</span>,
    cell: ({ row }) => {
      const cities = row.original.citiesDisplay;
      if (!cities?.length) return "-";
      return (
        <div className="flex flex-wrap gap-1">
          {cities.map((city) => (
            <Badge key={city} variant="secondary" className="text-xs">
              {city}
            </Badge>
          ))}
        </div>
      );
    },
  },
  {
    id: "enabled",
    accessorKey: "enabled",
    header: ({ column }) => SortUser("Statut", column),
    cell: ({ row }) => {
      return row.original.enabled ? (
        <Badge className="bg-green-600 text-white border-transparent">Activé</Badge>
      ) : (
        <Badge className="bg-red-600 text-white border-transparent">Désactivé</Badge>
      );
    },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: ({ column }) => SortUser("Créé le", column),
    cell: ({ row }) => {
      const date = row.original.createdAt;
      if (!date) return "-";
      return new Date(date).toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  {
    id: "actions",
    enableSorting: false,
    header: () => <div className="text-left">Action</div>,
    cell: ({ row }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
              size="icon"
            >
              <span className="sr-only">Open menu</span>
              <DotsHorizontalIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(row.original)} className="cursor-pointer">
              Modifier
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer">
              <ToggleEnabledButton serviceAccount={row.original} />
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer">
              <RegenerateKeyDialog
                serviceAccountId={row.original.id}
                serviceAccountName={row.original.name}
                onApiKeyGenerated={onApiKeyGenerated}
              />
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer">
              <DeleteServiceAccountDialog serviceAccountId={row.original.id} />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
